import { BridgeMarshalledDatasource, HasuraBaseQuery, NULL_DATA_STRING } from '@anduin/shared';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { getReasonInfo } from '../../../utils/unsent-email-reason.utils';
import { FailedEmailRow, FailedEmailStatus, RawFailedEmailRow } from '../../../models';

export class FailedEmailDatasource extends BridgeMarshalledDatasource<RawFailedEmailRow, FailedEmailRow> {
  constructor(apollo: Apollo, baseQuery: HasuraBaseQuery) {
    super(
      apollo,
      baseQuery,
      (raw: RawFailedEmailRow) => this.marshallRow(raw),
    );
  }

  getUndismissedCount(): Observable<number> {
    const entityName = this.baseQuery.entityName;
    const countQueryStr = `
      query { ${entityName}_aggregate(
        where: {
          dismissed_at: { _is_null: true }
          resolved_at: { _is_null: true }
        }
      ) {
        aggregate { count } }
      }
    `;

    return this.apollo
      .query<Record<string, { aggregate: { count: number } }>>({
        query: gql(countQueryStr),
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data[`${entityName}_aggregate`].aggregate.count),
        take(1),
      );
  }

  pollRetryingStatuses(ids: number[]): Observable<{ id: number; status: FailedEmailStatus }[]> {
    const entityName = this.baseQuery.entityName;
    const queryStr = `
      query PollRetryingStatuses($ids: [bigint!]!) {
        ${entityName}(where: { id: { _in: $ids } }) {
          id
          status
        }
      }
    `;

    return this.apollo
      .query<Record<string, { id: number; status: FailedEmailStatus }[]>>({
        query: gql(queryStr),
        variables: { ids },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data[entityName]),
        take(1),
      );
  }

  private marshallRow(raw: RawFailedEmailRow): FailedEmailRow {
    const info = getReasonInfo(raw.reasonCode);

    return {
      id: raw.id,
      status: raw.status,
      createdAt: raw.createdAt,
      reasonDisplay: info.displayText,
      recommendedAction: info.recommendedAction,
      subject: raw.subject || NULL_DATA_STRING,
      invoiceNumber: raw.invoiceNumber || NULL_DATA_STRING,
      recipient: raw.recipient || '',
      recipientCc: raw.recipientCc || '',
      recipientBcc: raw.recipientBcc || '',
      allRecipients: raw.allRecipients || '',
      reasonCode: raw.reasonCode,
      dismissedAt: raw.dismissedAt,
      resolvedAt: raw.resolvedAt,
      retryingSince: raw.retryingSince,
    };
  }
}
